import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { AuthContext } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Loader2, School } from 'lucide-react';
import type { Session } from '@/types/auth';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const TOKEN_REFRESH_THRESHOLD = 5 * 60; // 5 minutes in seconds
  const SESSION_CHECK_INTERVAL = 30 * 1000; // 30 seconds
  const LOADING_TIMEOUT = 3000; // 3 seconds

  const handleAuthError = useCallback((error: any) => {
    console.error('Auth error:', error);
    setSession(null);
    const publicPaths = ['/', '/auth/login', '/auth/forgot-password', '/auth/reset-password', '/track-issue'];
    const currentPath = window.location.pathname;
    if (!publicPaths.includes(currentPath)) {
      navigate('/auth/login');
    }
    setIsLoading(false);
  }, [navigate]);

  const fetchUserData = useCallback(async (userId: string, accessToken: string) => {
    try {
      const { data: userData, error } = await supabase
        .from('sms_users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      if (!userData) throw new Error('User data not found');

      return {
        user: userData,
        accessToken
      };
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }, []);

  // Function to check if token needs refresh
  const shouldRefreshToken = useCallback((session: any) => {
    if (!session?.expires_at) return false;
    const expiresAt = parseInt(session.expires_at);
    const now = Math.floor(Date.now() / 1000);
    return expiresAt - now < TOKEN_REFRESH_THRESHOLD;
  }, []);

  // Function to refresh token
  const refreshToken = useCallback(async () => {
    try {
      console.log('Refreshing token...');
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      if (!newSession) throw new Error('No session after refresh');
      
      const sessionData = await fetchUserData(
        newSession.user.id,
        newSession.access_token
      );
      setSession(sessionData);
    } catch (error) {
      console.error('Error refreshing token:', error);
      handleAuthError(error);
    }
  }, [fetchUserData, handleAuthError]);

  const checkAndRefreshSession = useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession && shouldRefreshToken(currentSession)) {
        await refreshToken();
      }
    } catch (error) {
      console.error('Error checking/refreshing session:', error);
    }
  }, [shouldRefreshToken, refreshToken]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let isSubscribed = true;

    const initSession = async () => {
      try {
        // Set timeout for loading state
        timeoutId = setTimeout(() => {
          if (isSubscribed) {
            setIsLoading(false);
            setSession(null);
            const publicPaths = ['/', '/auth/login', '/auth/forgot-password', '/auth/reset-password', '/track-issue'];
            if (!publicPaths.includes(window.location.pathname)) {
              navigate('/auth/login');
            }
          }
        }, LOADING_TIMEOUT);

        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session fetch timeout')), LOADING_TIMEOUT)
          )
        ]);

        // Clear timeout since we got a response
        clearTimeout(timeoutId);

        const { data: { session: currentSession }, error: sessionError } = sessionResult as any;

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          if (isSubscribed) {
            setSession(null);
            setIsLoading(false);
          }
          return;
        }
        
        if (!currentSession?.user) {
          if (isSubscribed) {
            setSession(null);
            setIsLoading(false);
            const publicPaths = ['/', '/auth/login', '/auth/forgot-password', '/auth/reset-password', '/track-issue'];
            if (!publicPaths.includes(window.location.pathname)) {
              navigate('/auth/login');
            }
          }
          return;
        }

        // Check if token needs refresh
        if (shouldRefreshToken(currentSession)) {
          await refreshToken();
        } else {
          try {
            const sessionData = await fetchUserData(
              currentSession.user.id,
              currentSession.access_token
            );
            if (isSubscribed) {
              setSession(sessionData);
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            handleAuthError(error);
          }
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Error fetching initial user data:', error);
        if (isSubscribed) {
          setSession(null);
          setIsLoading(false);
          if (!publicPaths.includes(window.location.pathname)) {
            navigate('/auth/login');
          }
        }
        return;
      }
      finally {
        if (isSubscribed) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    initSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, authSession) => {
      const publicPaths = ['/', '/auth/login', '/auth/forgot-password', '/auth/reset-password', '/track-issue'];
      const currentPath = window.location.pathname;

      // Skip auth state handling for public paths
      if (publicPaths.includes(currentPath)) {
        if (isSubscribed) {
          setSession(null);
        }
        return;
      }

      try {
        if (authSession?.user) {
          const sessionData = await fetchUserData(
            authSession.user.id,
            authSession.access_token
          );

          if (isSubscribed) {
            setSession(sessionData);
          }
        } else {
          if (isSubscribed) {
            setSession(null);
            if (window.location.pathname !== '/auth/login') {
              navigate('/auth/login');
            }
          }
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        handleAuthError(error);
      }
    });

    // Set up periodic session check
    const sessionCheckInterval = setInterval(checkAndRefreshSession, SESSION_CHECK_INTERVAL);

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
      clearInterval(sessionCheckInterval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate, fetchUserData, handleAuthError, refreshToken, shouldRefreshToken, checkAndRefreshSession]);

  if (isLoading) {
    const publicPaths = ['/', '/auth/login', '/auth/forgot-password', '/auth/reset-password', '/track-issue'];
    const currentPath = window.location.pathname;

    // Don't show loading screen for public paths
    if (publicPaths.includes(currentPath)) {
      return <>{children}</>;
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-blue-100">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center">
            <School className="h-12 w-12 text-primary animate-bounce" />
            <h1 className="mt-4 text-3xl font-bold">Teacher Reporter</h1>
            <p className="text-sm text-muted-foreground animate-pulse mt-4">
              Loading your experience...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}